import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useMutation, useQuery } from "@tanstack/react-query";

import MDBox from "components/MDBox";
import { Error, LoadingDialog } from "ui-components/Alert";
import { formRender, initialValues, stockHoldValidation } from "./stock-hold-schema";
import StockHoldInfo from "./components/StockHoldInfo";
import { openStockHoldColumns } from "./components/columns";
import MDTypography from "components/MDTypography";
import { StripedDataGrid } from "layouts/views/components/Grid";
import { getOpenStockHold } from "services/data.inventory.service";
import { createStockHold } from "services/data.internal.service";
import MDButton from "components/MDButton";
import styled from "@emotion/styled";
import { InboundSaveButton } from "ui-components/Form";
import dayjs from "dayjs";
import { Fieldset } from "ui-components/Form";
import { InboundLayout } from "ui-components/Layout";
import { Card, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { GridCellEditStartReasons } from "@mui/x-data-grid";
import { DeleteButton } from "ui-components/IconButton";
import { StockHoldCaption } from "layouts/views/inventory/components/Caption";
import { useLocation, useNavigate } from "react-router-dom";
import { Error_ } from "ui-components/Alert";

const EditableStyledDataGrid = styled(StripedDataGrid)({
  ".custom-editable-header": {
    backgroundColor: "#1A73E8 !important",
    color: "#fff",
    fontWeight: "bold",
    whiteSpace: "normal !important",
  },
  ".MuiDataGrid-columnHeaderTitle": {
    whiteSpace: "normal !important",
    lineHeight: 1,
  },
  ".MuiDataGrid-columnHeader": {
    backgroundColor: "#f0f2f5",
  },
  ".MuiDataGrid-cell": {
    whiteSpace: "normal !important",
    "word-wrap": "break-word !important",
  },
  ".right-align-header": {
    textAlign: "right",
  },
  ".highlight": {
    backgroundColor: "pink !important",
  },
});

const isCellEditable = (params) => {
  if (params.field === "unholdQuantity") {
    return params.row.remainHoldQuantity > 0;
  }
  return false;
};

function AddStockUnhold() {
  const location = useLocation();
  const navigate = useNavigate();
  const { stockHoldNo, customerCode } = location.state;
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [formValues, setFormValues] = useState(initialValues);
  const [detail, setDetail] = useState([]);
  const [inventory, setInventory] = useState([]);

  const errorColumns = [
    {
      field: "id",
      headerName: "LocAvaiID",
      sortable: false,
      align: "center",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "locationID",
      headerName: "Location ID",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "skuCode",
      headerName: "SKU Code",
      sortable: false,
      align: "left",
      flex: 1.8,
      minWidth: 180,
    },
    {
      field: "palletNbr",
      headerName: "Pallet Nbr",
      sortable: false,
      align: "left",
      flex: 1.2,
      minWidth: 112,
    },
    {
      field: "cartonID",
      headerName: "Carton ID",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "mln",
      headerName: "MLN",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "expiryDate",
      headerName: "Expiry Date",
      sortable: false,
      align: "left",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "message",
      headerName: "Message",
      sortable: false,
      align: "left",
      flex: 1.6,
      minWidth: 160,
    },
  ];

  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);
  const { isFetching } = useQuery({
    queryKey: ["stock-hold-log", { stockHoldNo, customerCode }],
    queryFn: () => getOpenStockHold({ stockHoldNo, customerCode }),
    onSuccess: (data) => {
      const { stockHoldDetails, ...values } = data.data.data;
      setFormValues({
        ...values,
      });
      setInventory(stockHoldDetails.map(x => {
        return { id: x.palletNbr, ...x }
      }));
    },
    onError,
  });

  const [errorItems, setErrorItems] = useState([]);

  const createStockHoldMutation = useMutation(createStockHold, {
    onSuccess: (data) => {
      navigate("/inventory/stock-hold", { state: data.data.message });
    },
    onError: (data) => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      const errorData = data.response.data.data;
      if (errorData) {
        setDialog(
          <Dialog open={true} maxWidth={"lg"}>
            <DialogTitle>{data.response.data.message}</DialogTitle>
            <DialogContent>
              <EditableStyledDataGrid
                columns={errorColumns}
                rows={errorData}
                sx={{
                  borderRadius: "0.75rem",
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                getRowHeight={() => "auto"}
                hideFooterSelectedRowCount={true}
              />
            </DialogContent>

            <DialogActions style={{ display: "flex", justifyContent: "center" }}>
              <MDButton
                color="info"
                style={{ width: "4rem" }}
                onClick={() => {
                  setDialog(null);
                }}
              >
                Ok
              </MDButton>
            </DialogActions>
          </Dialog>
        );
        const remainingDetail = detail.filter((row) => !errorData.some((err) => err.id === row.id));
        setDetail(remainingDetail);

        setErrorItems(errorData);
      }
      setAlert(<Error data={data} onChange={setAlert} />);
    },
  });

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(stockHoldValidation),
  });

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    trigger,
    reset,
    setValue,
  } = form;

  const infoProps = {
    control,
    errors,
    formRender,
    customerCode,
    setValue,
    mode: "EDIT",
  };

  const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return;
    }
    setDialog(<LoadingDialog />)

    const { ...data } = getValues();

    const req = {
      ...data,
      type: "UNHOLD",
      stockHoldDetails: detail.map((x) => {
        const { expiryDate, ...data } = x;
        return {
          expiryDate: expiryDate ? dayjs(expiryDate, "DD / MM / YYYY").format("YYYY-MM-DD") : null,
          ...data,
        };
      }),
    };

    createStockHoldMutation.mutate(req, {});
  };
  const createStatus = createStockHoldMutation.status === "loading";
  return (
    <InboundLayout lg={12}>
      {isFetching && <LoadingDialog />}
      {alert}
      {dialog}
      <Card sx={{ height: "100%" }}>
        <MDBox p={3}>
          <form noValidate>
            <StockHoldInfo {...infoProps} />
          </form>
        </MDBox>
      </Card>
      <MDBox sx={{ margin: "1rem 0" }} />
      <StockHoldDetail
        inventory={inventory}
        setInventory={setInventory}
        detail={detail}
        setDetail={setDetail}
        errorItems={errorItems}
        setErrorItems={setErrorItems}
      />
      <InboundSaveButton
        label="submit"
        disabled={createStatus || detail.length === 0}
        url="/inventory/stock-hold"
        onClick={onSubmit}
      />
    </InboundLayout>
  );
}

const StockHoldDetail = ({
  inventory,
  setInventory,
  detail,
  setDetail,
  errorItems,
  setErrorItems,
}) => {
  const [alert, setAlert] = useState(null);

  const handleProcessRowUpdateError = (error) => {
    setAlert(<Error content={error.message} onChange={setAlert} />);
  };

  const onCellEditStart = (params) => {
    if (params.value == null) {
      if (params.field === "unholdQuantity") {
        if (params.reason === GridCellEditStartReasons.cellDoubleClick) {
          const updatedInventory = inventory.map((row) => {
            if (row.id === params.row.id) {
              params.row.unholdQuantity = row.remainHoldQuantity;
              return { ...row, unholdQuantity: row.remainHoldQuantity };
            }
            return row;
          });
          setInventory(updatedInventory);
        }
      }
    }
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <InventoryGrid
          inventory={inventory}
          setInventory={setInventory}
          detail={detail}
          setDetail={setDetail}
          setAlert={setAlert}
          onCellEditStart={onCellEditStart}
          handleProcessRowUpdateError={handleProcessRowUpdateError}
          errorItems={errorItems}
          setErrorItems={setErrorItems}
        />
        {alert}
        <PreviewGrid
          inventory={inventory}
          setInventory={setInventory}
          detail={detail}
          setDetail={setDetail}
          handleProcessRowUpdateError={handleProcessRowUpdateError}
        />
      </MDBox>
    </Card>
  );
};

const InventoryGrid = ({
  inventory,
  setInventory,
  detail,
  setDetail,
  setAlert,
  onCellEditStart,
  handleProcessRowUpdateError,
  errorItems,
  setErrorItems,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);

  const handleRowUpdate = (updatedRow, originalRow) => {
    if (JSON.stringify(updatedRow) !== JSON.stringify(originalRow)) {
      if (!selectedIds.includes(updatedRow.id)) {
        setSelectedIds([...selectedIds, updatedRow.id]);
      }
      const newInventory = inventory.map((inv) => {
        if (inv.id === updatedRow.id) {
          if (updatedRow.unholdQuantity !== null && updatedRow.unholdQuantity > 0) {
            if (parseInt(originalRow.remainHoldQuantity) < parseInt(updatedRow.unholdQuantity)) {
              updatedRow.unholdQuantity = originalRow.remainHoldQuantity;
              originalRow.unholdQuantity = originalRow.remainHoldQuantity;
              inv.unholdQuantity = originalRow.remainHoldQuantity;
            } else {
              originalRow.unholdQuantity = updatedRow.unholdQuantity;
              inv.unholdQuantity = updatedRow.unholdQuantity;
            }
          } else {
            originalRow.unholdQuantity = null;
            inv.unholdQuantity = null;
          }
        }
        return inv;
      });
      setInventory(newInventory);
    }
    return originalRow;
  };

  const saveDetail = () => {
    const newDetail = inventory.filter((x) => x.unholdQuantity !== null && x.unholdQuantity != 0);

    const unsuccessfulDetail = inventory.filter((x) =>
      selectedIds.some(y => y === x.id) && !newDetail.some(y => y.id === x.id)).map(x => {
        return x.skuCode + ': No Unhold Quantity'
      });

    if (unsuccessfulDetail.length > 0) {
      const errorMsg = (
        <>
          {unsuccessfulDetail.map((line, index) => (
            <span key={index}>
              {line}
              <br />
            </span>
          ))}
        </>
      );
      setAlert(<Error_ content={errorMsg} onChange={setAlert} />);
      if (newDetail.length === 0) {
        return;
      }
    }

    if (detail.length > 0) {
      let modifiedDetail = JSON.parse(JSON.stringify(detail));

      const filteredInv = newDetail.filter((x) => !detail.some((y) => y.id === x.id));

      modifiedDetail = [...modifiedDetail, ...filteredInv];

      setDetail(modifiedDetail);
    } else {
      setDetail(newDetail);
    }

    setErrorItems([]);

    const leftInventory = inventory.filter(
      (x) =>
        (x.unholdQuantity === null || x.unholdQuantity === 0)
    );
    if (leftInventory.length < inventory.length) {
      setInventory(leftInventory);
    }
  };
  const selectRow = (id) => {
    if (selectedIds.length > id.length) {
      const removed = selectedIds.filter((x) => !id.includes(x));
      let updatedRow = inventory;
      removed.forEach((removedID) => {
        updatedRow = updatedRow.map((x) => {
          if (x.id === removedID) {
            return {
              ...x,
              unholdQuantity: null,
            };
          } else return x;
        });
      });
      setInventory(updatedRow);
    }
    setSelectedIds(id);
  };

  const onCellEditEnd = (params) => {
    if (params.row.unholdQuantity > 0 && !selectedIds.some((x) => x === params.row.id)) {
      setSelectedIds([...selectedIds, params.row.id]);
    }
  };

  return (
    <Fieldset label={"Stock Unhold Detail"}>
      <MDTypography mt={1.625} mb={2} color="info" variant="h5">
        Stock Hold Datagrid Display
      </MDTypography>
      <EditableStyledDataGrid
        columns={openStockHoldColumns}
        rows={inventory}
        checkboxSelection={true}
        disableRowSelectionOnClick={true}
        onRowSelectionModelChange={selectRow}
        rowSelectionModel={selectedIds}
        sx={{
          borderRadius: "0.75rem",
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        getRowHeight={() => "auto"}
        processRowUpdate={handleRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        hideFooterSelectedRowCount={true}
        isCellEditable={isCellEditable}
        onCellEditStart={onCellEditStart}
        onCellEditStop={onCellEditEnd}
        getRowClassName={(params) => {
          return errorItems.some((x) => x.id === params.row.id) ? "highlight" : "";
        }}
      />
      <MDBox mt={2} width="100%" display="flex" justifyContent="center">
        <MDButton variant="gradient" color="info" onClick={saveDetail}>
          Add
        </MDButton>
      </MDBox>
    </Fieldset>
  );
};

const PreviewGrid = ({
  inventory,
  setInventory,
  detail,
  setDetail,
  handleProcessRowUpdateError,
}) => {
  const addBackInventory = (req) => {
    const { unholdQuantity, ...data } = req;
    const updatedInventory = [...inventory, { unholdQuantity: null, ...data }];
    setInventory(updatedInventory);
  };

  const handleDeleteClick = (req) => {
    addBackInventory(req);
    setDetail(detail.filter((row) => !(row.id === req.id)));
  };
  const previewColumn = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 80,
      cellClassName: "actions",
      getActions: ({ row }) => {
        return [
          <DeleteButton
            title="Delete"
            onClick={() => {
              handleDeleteClick(row);
            }}
          />,
        ];
      },
    },
    ...openStockHoldColumns,
  ];

  const handleRowUpdate = (updatedRow, originalRow) => {
    if (!(updatedRow.unholdQuantity && updatedRow.unholdQuantity > 0)) {
      handleDeleteClick(updatedRow);
      return { ...originalRow, _action: "delete" };
    } else {
      const newDetail = detail.map((det) => {
        if (det.id === updatedRow.id) {
          if (updatedRow.unholdQuantity !== null && updatedRow.unholdQuantity > 0) {
            if (parseInt(originalRow.remainHoldQuantity) < parseInt(updatedRow.unholdQuantity)) {
              updatedRow.unholdQuantity = originalRow.remainHoldQuantity;
              originalRow.unholdQuantity = originalRow.remainHoldQuantity;
              det.unholdQuantity = originalRow.remainHoldQuantity;
            } else {
              originalRow.unholdQuantity = updatedRow.unholdQuantity;
              det.unholdQuantity = updatedRow.unholdQuantity;
            }
          }
        }
        return det;
      });
      setDetail(newDetail);
    }
    return originalRow;
  };

  const caption = () => {
    const totalHold =
      detail.length > 0
        ? detail.reduce((total, det) => {
          return total + det.remainHoldQuantity;
        }, 0)
        : 0;
    const totalUnhold =
      detail.length > 0
        ? detail.reduce((total, det) => {
          return total + det.unholdQuantity;
        }, 0)
        : 0;
    return (
      <StockHoldCaption totalRows={detail.length} totalHold={totalHold} totalUnhold={totalUnhold} />
    );
  };

  return (
    <MDBox p={3}>
      <MDTypography mt={1.625} mb={2} color="info" variant="h5">
        Stock Unhold Detail Preview
      </MDTypography>
      <EditableStyledDataGrid
        columns={previewColumn}
        rows={detail}
        sx={{
          borderRadius: "0.75rem",
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        getRowHeight={() => "auto"}
        processRowUpdate={handleRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        hideFooterSelectedRowCount={true}
        isCellEditable={isCellEditable}
        slots={{
          footer: caption,
        }}
      />
    </MDBox>
  );
};

export default AddStockUnhold;
